import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import '@cyntox/common/styles.css';
import { Gate } from '@cyntox/common/platform';
import { getAuthConfig, initAuth } from './api/auth';
import { AppProvider, useApp } from './state/AppContext';
import { Shell } from './components/Shell';
import { DataImport } from './screens/DataImport';
import { Employees } from './screens/Employees';
import { Schedules } from './screens/Schedules';
import { Facilities } from './screens/Facilities';
import { PayCodes } from './screens/PayCodes';
import { Administration } from './screens/Administration';
import { AuditLog } from './screens/AuditLog';
import { Help } from './screens/Help';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/** Sends each role to the first Admin screen it can actually see. */
function Landing() {
  const { can } = useApp();
  const first =
    (can('dataImport', 'view') && '/import') ||
    (can('employees', 'view') && '/employees') ||
    (can('schedules', 'view') && '/schedules') ||
    (can('users', 'view') && '/admin') ||
    (can('audit', 'view') && '/audit') ||
    (can('reference', 'view') && '/facilities') ||
    null;
  return first ? <Navigate to={first} replace /> : <NothingHere />;
}

/**
 * Signed in, with a role, but nothing in that role belongs to Admin — a
 * supervisor who only reviews daily records, say. Point them where their
 * access does lead rather than showing an empty menu.
 */
function NothingHere() {
  const scorecard = getAuthConfig()?.apps?.scorecard;
  return (
    <div className="card" style={{ maxWidth: 520 }}>
      <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Nothing in Admin for your access</div>
      <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-secondary)', margin: 0 }}>
        Your role doesn't include any of Admin's screens.
        {scorecard && <> Your work is in the <a href={scorecard}>Facility Scorecard</a>.</>}
      </p>
    </div>
  );
}

function render() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppProvider>
            <Routes>
              <Route element={<Shell />}>
                <Route index element={<Landing />} />
                {/* Each screen checks its capability at the route, so a typed URL
                    without access says so instead of showing an empty list. */}
                <Route path="/import" element={<Gate any={[['dataImport', 'view']]}><DataImport /></Gate>} />
                <Route path="/employees" element={<Gate any={[['employees', 'view']]}><Employees /></Gate>} />
                <Route path="/schedules" element={<Gate any={[['schedules', 'view']]}><Schedules /></Gate>} />
                <Route path="/facilities" element={<Gate any={[['reference', 'view']]}><Facilities /></Gate>} />
                <Route path="/paycodes" element={<Gate any={[['reference', 'view']]}><PayCodes /></Gate>} />
                <Route path="/admin" element={<Gate any={[['users', 'view']]}><Administration /></Gate>} />
                <Route path="/audit" element={<Gate any={[['audit', 'view']]}><AuditLog /></Gate>} />
                {/* No capability: everyone who can sign in can read the guide. */}
                <Route path="/help" element={<Help />} />
                <Route path="*" element={<Landing />} />
              </Route>
            </Routes>
          </AppProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// Nothing renders until we know how to authenticate. With Entra that means a
// token is in hand; without it, the browser has already left for Microsoft and
// this page is being replaced.
initAuth().then(render).catch((err: Error) => {
  document.getElementById('root')!.innerHTML =
    `<div style="font:14px system-ui;padding:40px;max-width:640px">`
    + `<h2 style="margin:0 0 8px">Admin could not start</h2>`
    + `<p style="color:#5a5852">${err.message}</p>`
    + `<p style="color:#9a9890;font-size:12px">Check that the API is running and reachable.</p>`
    + `</div>`;
});
